import React, { useEffect, useState } from "react";
import { Coins, RefreshCw } from "lucide-react";
import { adminApi } from "../lib/api";
import { inr } from "../lib/format";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" }) : "—");

// Admin "Toss Settlement": every match with open toss bets, Jitex's toss text + suggested
// winner, and one-click settle (pay winners 2x) or void (refund all).
export default function AdminToss({ token }) {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = () => adminApi.tossPending(token).then(setRows).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const settle = async (m, winner) => {
    const label = winner === "VOID" ? "VOID (refund every toss bet)" : `${winner} won the toss`;
    if (!window.confirm(`Settle toss for ${m.label} as: ${label}?`)) return;
    setBusy(m.matchId + winner);
    try {
      const r = await adminApi.settleToss(token, m.matchId, winner);
      toast({ title: "Toss settled", description: `${m.label}: ${r.won} won · ${r.lost} lost · ${r.void} refunded` });
      load();
    } catch (e) {
      toast({ title: "Could not settle", description: e?.response?.data?.detail || "Error" });
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="admin-toss-panel">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-semibold text-lg inline-flex items-center gap-2"><Coins className="h-5 w-5 text-sky-400" /> Toss Settlement</h2>
        <Button size="sm" variant="outline" onClick={load} data-testid="admin-toss-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card p-10 text-center text-muted-foreground" data-testid="admin-toss-empty">No open toss bets.</div>
      ) : rows.map((m) => (
        <div key={m.matchId} className="rounded-2xl border border-sky-500/30 bg-card p-5" data-testid={`admin-toss-row-${m.matchId}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="font-display font-bold">{m.label} <span className="text-xs font-normal text-muted-foreground ml-2">starts {fmt(m.startTime)} · {m.status}</span></div>
              <div className="text-sm mt-1">
                <span className="text-muted-foreground">Jitex toss:</span>{" "}
                {m.toss.text ? <span className="text-foreground" data-testid={`admin-toss-text-${m.matchId}`}>{m.toss.text}</span> : <span className="text-amber-300">not published yet</span>}
                {m.toss.suggestedWinner && <span className="ml-2 rounded bg-sky-500/20 text-sky-300 px-1.5 py-0.5 text-xs font-bold" data-testid={`admin-toss-suggested-${m.matchId}`}>suggested: {m.toss.suggestedWinner}</span>}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {m.openBets} open bets · {inr(m.openStake)} staked · {Object.entries(m.stakeByTeam).map(([k, v]) => `${k} ${inr(v)}`).join(" / ")}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {[m.homeTeam, m.awayTeam].filter(Boolean).map((t) => (
                <Button key={t.code} size="sm" disabled={!!busy} onClick={() => settle(m, t.code)}
                  className={`font-semibold ${m.toss.suggestedWinner === t.code ? "bg-sky-500 hover:bg-sky-600 text-white" : ""}`}
                  variant={m.toss.suggestedWinner === t.code ? "default" : "outline"} data-testid={`admin-toss-settle-${m.matchId}-${t.code}`}>
                  {t.name} won
                </Button>
              ))}
              <Button size="sm" variant="destructive" disabled={!!busy} onClick={() => settle(m, "VOID")} data-testid={`admin-toss-void-${m.matchId}`}>Void (refund)</Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
